import clsx from 'clsx';
import React from 'react';
import { describeWhen, formatShortDateTime } from '../../shared/time';
import { IconClock } from './icons';

/**
 * A moment in a list row: clock glyph, then "Today, 6:00 PM" / "Yesterday,
 * 2:45 PM" / "Oct 3, 7:00 PM". One time language for every list.
 *
 * Colour rule: warm (`warning`) marks today and tomorrow — the rows a
 * restaurant acts on now. Past days step back to `fg-2`; further-out days
 * keep the base colour with a medium weight. `muted` (a cancelled booking,
 * a rejected order) drops the whole cell to `fg-3` with no warm mark.
 *
 * `stacked` puts the time first, bold, over the day in smaller type — for
 * lists where the time is what a person scans for (reservations). Needs a
 * row tall enough for two lines (`lg`).
 */
export const When: React.FC<{
  value?: string | null;
  muted?: boolean;
  stacked?: boolean;
  className?: string;
}> = ({ value, muted, stacked, className }) => {
  const when = describeWhen(value);
  if (!when) {
    const raw = formatShortDateTime(value);
    return raw ? (
      <span className={clsx('text-v2-fg-2', className)}>{raw}</span>
    ) : (
      <span className={clsx('text-v2-fg-4', className)}>—</span>
    );
  }
  const soon = !muted && (when.day === 'today' || when.day === 'tomorrow');
  const past = when.day === 'past' || when.day === 'yesterday';
  if (stacked) {
    return (
      <span
        className={clsx(
          'inline-flex flex-col whitespace-nowrap leading-tight',
          className,
        )}
      >
        <span
          className={clsx(
            'text-v2-14 font-semibold',
            muted ? 'text-v2-fg-3' : past ? 'text-v2-fg-2' : 'text-v2-fg',
          )}
        >
          {when.time}
        </span>
        <span
          className={clsx(
            'text-v2-12',
            soon ? 'font-semibold text-v2-warning' : 'text-v2-fg-3',
          )}
        >
          {when.dayLabel}
        </span>
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 whitespace-nowrap',
        className,
      )}
    >
      <IconClock
        size={13}
        className={clsx('shrink-0', soon ? 'text-v2-warning' : 'text-v2-fg-4')}
      />
      <span
        className={clsx(
          muted ? 'text-v2-fg-3' : past ? 'text-v2-fg-2' : 'text-v2-fg',
        )}
      >
        <span
          className={clsx(
            soon && 'font-semibold text-v2-warning',
            !muted && when.day === 'later' && 'font-medium',
          )}
        >
          {when.dayLabel}
        </span>
        <span className="text-v2-fg-4">, </span>
        <span className={clsx(!muted && !past && 'font-medium')}>
          {when.time}
        </span>
      </span>
    </span>
  );
};
